import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shield, BarChart3, Settings2, Megaphone, Lock } from "lucide-react";
import { CookiePreferences } from "@/hooks/useCookieConsent";

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (preferences: Partial<CookiePreferences>) => void;
  currentPreferences: CookiePreferences | null;
}

interface CookieCategory {
  id: keyof Omit<CookiePreferences, 'timestamp' | 'version'>;
  name: string;
  description: string;
  icon: React.ReactNode;
  required: boolean;
  enabled: boolean;
}

export const CookiePreferencesModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  currentPreferences 
}: CookiePreferencesModalProps) => {
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(false);

  // Initialize state from current preferences
  useEffect(() => {
    if (currentPreferences) {
      setAnalytics(currentPreferences.analytics);
      setFunctional(currentPreferences.functional);
    }
  }, [currentPreferences, isOpen]);

  const handleSave = () => {
    onSave({
      analytics,
      functional,
      marketing: false, // Not used yet
    });
  };

  const handleAcceptAll = () => {
    onSave({
      analytics: true,
      functional: true,
      marketing: false,
    });
  };

  const categories: CookieCategory[] = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'Required for login, security, and core functionality. These cookies cannot be disabled as they are necessary for the website to function properly.',
      icon: <Shield className="h-5 w-5 text-neon-glow" />,
      required: true,
      enabled: true,
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Used to understand how visitors use the site. This helps us improve our website and services. NO marketing or advertising tracking is included.',
      icon: <BarChart3 className="h-5 w-5 text-radiant-blue" />,
      required: false,
      enabled: analytics,
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'Improve your user experience by remembering your preferences and settings, such as language or theme choices.',
      icon: <Settings2 className="h-5 w-5 text-purple-400" />,
      required: false,
      enabled: functional,
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg bg-deep-navy border-neon-glow/20 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-neon-glow" />
            Cookie Preferences
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Manage your cookie preferences. You can enable or disable different types of cookies below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {categories.map((category) => (
            <div 
              key={category.id}
              className="p-4 rounded-lg bg-background/30 border border-neon-glow/10 hover:border-neon-glow/20 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="mt-0.5">{category.icon}</div>
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-white font-semibold">
                        {category.name}
                      </Label>
                      {category.required && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neon-glow/10 text-neon-glow text-xs">
                          <Lock className="h-3 w-3" />
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {category.description}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={category.enabled}
                  disabled={category.required}
                  onCheckedChange={(checked) => {
                    if (category.id === 'analytics') setAnalytics(checked);
                    if (category.id === 'functional') setFunctional(checked);
                  }}
                  className="data-[state=checked]:bg-neon-glow"
                />
              </div>
            </div>
          ))}

          {/* Future Marketing Section - Hidden for now */}
          {false && (
            <div className="p-4 rounded-lg bg-background/30 border border-gray-700/30 opacity-50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <Megaphone className="h-5 w-5 text-gray-500 mt-0.5" />
                  <div className="space-y-1">
                    <Label className="text-gray-500 font-semibold">
                      Marketing Cookies
                    </Label>
                    <p className="text-sm text-gray-600">
                      Coming soon - not currently in use.
                    </p>
                  </div>
                </div>
                <Switch disabled className="opacity-50" />
              </div>
            </div>
          )}
        </div>

        <div className="text-xs text-gray-500 border-t border-neon-glow/10 pt-4">
          <p>
            Your preferences are stored locally and can be changed at any time.
            For more information, please read our{" "}
            <a href="/cookie-policy" className="text-neon-glow hover:underline">
              Cookie Policy
            </a>
            .
          </p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-gray-600 hover:border-gray-500 text-gray-300"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleSave}
            className="border-neon-glow/40 hover:border-neon-glow text-white"
          >
            Save Preferences
          </Button>
          <Button
            onClick={handleAcceptAll}
            className="bg-gradient-to-r from-neon-glow to-radiant-blue hover:opacity-90 text-deep-navy font-semibold"
          >
            Accept All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
